/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class DataModel
 * @hideconstructor
 * @classdesc Provides access to the data model
 */
define(['../UniqueID', '../../../lib/@waca/dashboard-common/dist/core/UniqueHashIdBuilder'], (UniqueID, UniqueHashIdBuilder) => {

	return class DataModel {
		constructor(dataModelJson) {
			this._dataModel = dataModelJson;
			this._currentIdList = this._processCurrentDataItemIds();
		}

		_processCurrentDataItemIds() {
			const idList = [];
			const dataViews = this._dataModel.dataViews;
			if (dataViews){
				for (let i = 0 ; i < dataViews.length ; i++){
					if (!dataViews[i].id){
						dataViews[i].id = UniqueID.generate('model');
					}
					const dataItems = dataViews[i].dataItems;
					if (dataItems){
						for (let j = 0 ; j < dataItems.length ; j++){
							if (!dataItems[j].id){
								let dataItemId = UniqueHashIdBuilder.createUniqueHashId(dataItems[j].itemId, idList);
								dataItems[j].id = dataItemId;
							}
							idList.push(dataItems[j].id);
						}
					}
				}
			}
			return idList;
		}

		setModelRefInAllViews(modelRef) {
			const dataViews = this._dataModel.dataViews;
			let isSet = false;
			if (dataViews){
				for (let i = 0 ; i < dataViews.length ; i++){
					isSet = true;
					dataViews[i].modelRef = modelRef;
				}
			}
			if (!isSet){
				// create a default data view and set the model ref
				this.createDataView().modelRef = modelRef;
			}
		}

		/**
		 * 
		 * @param {string} viewId 
		 * @param {boolean} removeAll remove all data views if true. 
		 */
		removeDataView(viewId, removeAll) {
			if (removeAll) {
				this._dataModel.dataViews.length = 0;
			} else {
				const dataViews = this._dataModel.dataViews;
				if (dataViews){
					this._dataModel.dataViews = dataViews.filter(dataView => {return dataView.id !== viewId;});
				}
			}
		}

		getDataView(viewId) {
			let dataView = null;
			const dataViews = this._dataModel.dataViews;
			if (dataViews){
				for (let i = 0 ; i < dataViews.length ; i++){
					// If view id is not provided, default to first view id.
					if (!viewId && i === 0 || dataViews[i].id === viewId) {
						dataView = dataViews[i];
						break;
					}
				}
			}

			return dataView;
		}

		createDataView(id) {
			if (!this._dataModel.dataViews) {
				this._dataModel.dataViews = [];
			}
			const dataView = {
				id : id || UniqueID.generate('model')
			};
			this._dataModel.dataViews.push(dataView);
			return dataView;
		}

		moveDataItemsToView(dataItemIds, viewId){
			dataItemIds.forEach(id => {
				const info = this._getDataItemInfo(id);
				if (info.dataView.id !== viewId) {
					this.addDataItem(info.dataItem, viewId);
				}
			});
		}

		getDataViewIdForDataItem(dataItemId){
			const info = this._getDataItemInfo(dataItemId);
			return info ? info.dataView.id : null;
		}

		getDataItem(dataItemId) {
			const info = this._getDataItemInfo(dataItemId);
			return info ? info.dataItem : null;
		}

		removeDataItem(id, hasLocalFilter) {
			let removed;
			const info = this._getDataItemInfo(id);
			if (info){
				info.dataView.dataItems.splice(info.dataItemIndex, 1);

				const idIndex = this._currentIdList.indexOf(id);
				if (idIndex !== -1){
					this._currentIdList.splice(idIndex, 1);
				}

				removed = info.dataItem;
				if (info.dataView.dataItems.length === 0 && !hasLocalFilter){
					// remove the empty dataView
					this.removeDataView(info.dataView.id);
				}
			}

			return removed;
		}

		addDataItem(dataItem, viewId){

			if(!dataItem.id){
				// We generate a predicatable unique id based on the column id
				dataItem.id = UniqueHashIdBuilder.createUniqueHashId(dataItem.columnId, this._currentIdList);
			}
			this._currentIdList.push(dataItem.id);

			// First remove it if it already exists
			let model = this.removeDataItem(dataItem.id);
			if (!model){
				// Pick only id, itemId, columnId, itemLabel and navigationPathId
				// Eventually we will support more..
				model = (({ id, itemId, columnId, itemLabel, navigationPathId }) => ({ id, itemId, columnId, itemLabel, navigationPathId }))(dataItem);

				// The internal spec use itemId instead of columnId
				if (model.columnId){
					model.itemId = model.columnId;
					delete model.columnId;
				}
			}

			// Find or create the target dataView
			let dataView = this.getDataView(viewId);

			if (!dataView) {
				dataView = this.createDataView();
			}

			if (!dataView.dataItems) {
				dataView.dataItems = [];
			}

			dataView.dataItems.push(model);

			return model;
		}

		getDataItemList(){
			const result = [];
			const dataViews = this._dataModel.dataViews;
			if (dataViews){
				for (let i = 0 ; i < dataViews.length ; i++){
					const dataItems = dataViews[i].dataItems;
					if (dataItems){
						for (let j = 0 ; j < dataItems.length ; j++){
							result.push(dataItems[j]);

						}
					}
				}
			}
			return result;
		}


		_getDataItemInfo(dataItemId){
			let dataItemInfo = null;
			const dataViews = this._dataModel.dataViews;
			if (dataViews){
				for (let i = 0 ; i < dataViews.length ; i++){
					const dataItems = dataViews[i].dataItems;
					if (dataItems){
						for (let j = 0 ; j < dataItems.length ; j++){
							if (dataItems[j].id === dataItemId){
								dataItemInfo = {
									dataItem: dataItems[j],
									dataView: dataViews[i],
									dataItemIndex: j,
									dataViewIndex: i
								};
								break;
							}
						}
					}
					if (dataItemInfo){
						break;
					}
				}
			}
			return dataItemInfo;
		}
	};
});
